import React, { useCallback, useEffect, useMemo, useState } from "react";
import ComplianceMonitorService from "../../services/ComplianceMonitorService";
import Icons from "../../components/UI/Icons";
import FormInputs from "../../components/UI/FormInputs";
import Alert from "../../components/UI/ConfirmBox/Alert";

const statusTextClassMap = {
  Active: "sh_dashtext-green",
  "Due Soon": "sh_dashtext-yellow",
  Overdue: "sh_dashtext-red",
};

const PatientComplianceExams = ({ employeeId, Loader, Toast, registerComplianceBeforeSave }) => {
  const [records, setRecords] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [reminderConfirmItem, setReminderConfirmItem] = useState(null);
  const [activityOptions, setActivityOptions] = useState([]);
  const [newExamDraft, setNewExamDraft] = useState({
    activity_id: "",
    last_exam_taken: "",
    due_date: "",
  });

  const toUiDate = (apiDate) => {
    if (apiDate == null || apiDate === "") return "";
    const str = String(apiDate).trim();
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
      return str;
    }
    const ymd = str.includes("T") ? str.slice(0, 10) : str.length >= 10 && str.includes("-") ? str.slice(0, 10) : str;
    if (!ymd.includes("-")) return "";
    const [year, month, day] = ymd.split("-");
    if (!year || !month || !day) return "";
    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  };

  const toApiDate = (uiDate) => {
    if (!uiDate || !uiDate.includes("/")) return "";
    const [month, day, year] = uiDate.split("/");
    const mm = String(month).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  const fetchRecords = async () => {
    if (!employeeId) return;
    Loader(true);
    const response = await ComplianceMonitorService.getPatientComplianceExams(null, employeeId);
    Loader(false);
    const list = response?.records || [];
    setRecords(list);

    const nextDrafts = {};
    list.forEach((item) => {
      nextDrafts[item.activity_id] = {
        due_date: toUiDate(item.due_date) || "",
      };
    });
    setDrafts(nextDrafts);
    setEditingRows({});
  };

  const fetchActivityOptions = async () => {
    try {
      const response = await ComplianceMonitorService.getList(null, {
        page: 1,
        perPage: 500,
        sort_by: "activity_name",
        sort_order: "asc",
      });
      const payload = response || {};
      const paginator = payload?.data?.data ? payload.data : payload;
      const list = Array.isArray(paginator?.data) ? paginator.data : [];
      const options = list.map((item) => ({
        label: item.activity_name || "Unknown Activity",
        value: item.activity_id || "",
        allow_override: !!item?.allow_override,
      }));
      setActivityOptions(options.filter((item) => item.value));
    } catch (e) {
      setActivityOptions([]);
      Toast.error("Unable to load compliance activities.");
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [employeeId]);

  useEffect(() => {
    fetchActivityOptions();
  }, []);

  const hasRows = useMemo(() => records.length > 0, [records]);

  const onChangeDueDateDraft = (activityId, value) => {
    setDrafts((prev) => ({
      ...prev,
      [activityId]: {
        ...(prev[activityId] || {}),
        due_date: value,
      },
    }));
  };

  const lastExamTakenForApi = (item) => toApiDate(toUiDate(item.last_exam_taken));

  const saveOverride = async (item, options = {}) => {
    const { silent = false, shouldRefresh = true } = options;
    const draft = drafts[item.activity_id] || {};
    const lastExamApi = lastExamTakenForApi(item);
    if (!lastExamApi) {
      if (!silent) {
        Toast.error("Last Exam Taken is required before due date can be updated.");
      }
      return false;
    }

    Loader(true);
    const res = await ComplianceMonitorService.overridePatientComplianceExam(
      null,
      employeeId,
      item.activity_id,
      {
        last_exam_taken: lastExamApi,
        due_date: draft.due_date ? toApiDate(draft.due_date) : null,
      }
    );
    Loader(false);

    if (res?.status === "ERROR") {
      if (!silent) {
        Toast.error(res?.data || "Unable to save manual override.");
      }
      return false;
    }

    if (!silent) {
      Toast.success("Compliance exam updated successfully.");
    }
    setEditingRows((prev) => ({ ...prev, [item.activity_id]: false }));
    if (shouldRefresh) {
      await fetchRecords();
    }
    return true;
  };

  const startEdit = (activityId) => {
    setEditingRows((prev) => ({ ...prev, [activityId]: true }));
  };

  const cancelEdit = (activityId, item) => {
    setDrafts((prev) => ({
      ...prev,
      [activityId]: {
        due_date: toUiDate(item.due_date) || "",
      },
    }));
    setEditingRows((prev) => ({ ...prev, [activityId]: false }));
  };

  const toggleReminders = async (item) => {
    const next = !item.reminders_disabled;
    Loader(true);
    const res = await ComplianceMonitorService.togglePatientComplianceReminders(
      null,
      employeeId,
      item.activity_id,
      next
    );
    Loader(false);

    if (res?.status === "ERROR") {
      Toast.error(res?.data || "Unable to update reminder preference.");
      return;
    }

    Toast.success(next ? "Reminders muted for this exam." : "Exam reminders enabled.");
    fetchRecords();
  };

  const openReminderConfirm = (item) => {
    setReminderConfirmItem(item);
  };

  const closeReminderConfirm = () => {
    setReminderConfirmItem(null);
  };

  const confirmReminderToggle = async () => {
    if (!reminderConfirmItem) return;
    const item = reminderConfirmItem;
    closeReminderConfirm();
    await toggleReminders(item);
  };

  const savePendingEditsForParent = useCallback(async () => {
    const pendingItems = records.filter((item) => item.allow_override && editingRows[item.activity_id]);
    if (pendingItems.length === 0) {
      return true;
    }

    for (const item of pendingItems) {
      const ok = await saveOverride(item, { silent: true, shouldRefresh: false });
      if (!ok) {
        Toast.error(`Unable to save compliance exam changes for ${item.activity_name || "activity"}.`);
        return false;
      }
    }

    await fetchRecords();
    Toast.success("Compliance exam changes saved.");
    return true;
  }, [records, editingRows, drafts, employeeId]);

  useEffect(() => {
    if (typeof registerComplianceBeforeSave === "function") {
      registerComplianceBeforeSave(savePendingEditsForParent);
      return () => registerComplianceBeforeSave(null);
    }
    return undefined;
  }, [registerComplianceBeforeSave, savePendingEditsForParent]);

  const reminderConfirmCopy = !reminderConfirmItem
    ? ""
    : reminderConfirmItem.reminders_disabled
      ? `Turn on SMS/email reminders for "${reminderConfirmItem.activity_name || "this activity"}"?`
      : `Mute reminders for "${reminderConfirmItem.activity_name || "this exam"}"? This will stop reminders until you turn them back on.`;

  const createComplianceExam = async () => {
    if (!employeeId) {
      Toast.error("Select an employee first.");
      return;
    }
    if (!newExamDraft.activity_id) {
      Toast.error("Activity Name is required.");
      return;
    }
    if (!newExamDraft.last_exam_taken) {
      Toast.error("Last Exam Taken is required.");
      return;
    }
    if (!newExamDraft.due_date) {
      Toast.error("Due Date is required.");
      return;
    }

    Loader(true);
    const response = await ComplianceMonitorService.overridePatientComplianceExam(
      null,
      employeeId,
      newExamDraft.activity_id,
      {
        last_exam_taken: toApiDate(newExamDraft.last_exam_taken),
        due_date: toApiDate(newExamDraft.due_date),
        bypass_override_check: true,
      }
    );
    Loader(false);

    if (response?.status === "ERROR") {
      Toast.error(response?.data || "Unable to add compliance exam.");
      return;
    }

    Toast.success("Compliance exam added successfully.");
    setNewExamDraft({
      activity_id: "",
      last_exam_taken: "",
      due_date: "",
    });
    await fetchRecords();
    await fetchActivityOptions();
  };

  return (
    <div className="sh_innerPortview">
      {reminderConfirmItem && (
        <Alert
          type="warning"
          custonTitle="Reminder settings"
          contentTitle={reminderConfirmCopy}
          onClose={closeReminderConfirm}
          onDelete={confirmReminderToggle}
          yesButtonName="Confirm"
          noButtonName="Cancel"
        />
      )}
      <div className="sh_cardBox h-auto shadow-none border">
        <div className="sh_cardHeader">
          <div className="form-group d-flex mb-0 justify-content-between align-items-center">
            <h6 className="text-md mb-0">Compliance / Exams</h6>
          </div>
          <div className="row mt-2">
            <div className="col-lg-4 col-md-6 mb-2">
              <div className="form-group mb-0">
                <label
                  htmlFor="patient-compliance-new-activity"
                  className="text-sm text-truncate mb-0 opacity-75"
                >
                  Activity Name
                </label>
                <FormInputs
                  id="patient-compliance-new-activity"
                  type="SelectList"
                  options={[{ label: "Select Activity", value: "" }, ...activityOptions]}
                  value={newExamDraft.activity_id}
                  changeHandler={(value) =>
                    setNewExamDraft((prev) => ({ ...prev, activity_id: value }))
                  }
                  name="activity_id"
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mb-2">
              <div className="form-group mb-0">
                <label className="text-sm text-truncate mb-0 opacity-75">Last Exam Taken</label>
                <FormInputs
                  type="Date"
                  value={newExamDraft.last_exam_taken}
                  changeHandler={(value) =>
                    setNewExamDraft((prev) => ({ ...prev, last_exam_taken: value }))
                  }
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6 mb-2">
              <div className="form-group mb-0">
                <label className="text-sm text-truncate mb-0 opacity-75">Due Date</label>
                <FormInputs
                  type="Date"
                  value={newExamDraft.due_date}
                  changeHandler={(value) =>
                    setNewExamDraft((prev) => ({ ...prev, due_date: value }))
                  }
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6 mb-2 d-flex flex-column justify-content-end">
              <div className="form-group mb-0 w-100">
                <label className="text-sm text-truncate mb-0 opacity-75 invisible" aria-hidden="true">
                  &nbsp;
                </label>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={createComplianceExam}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="sh_cardBody p-0">
          {!hasRows ? (
            <div className="p-3 text-muted text-center">No compliance exams configured for this patient.</div>
          ) : (
            <table
              className="table dataTable sh_tablenew nowrap table-hover my-0"
              width="100%"
              cellSpacing="0"
              style={{ width: "100%" }}
            >
              <thead>
                <tr>
                  <th>Activity Name</th>
                  <th>Last Exam Taken</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map((item) => {
                  const draft = drafts[item.activity_id] || {};
                  const canEdit = !!item.allow_override;
                  const isEditing = !!editingRows[item.activity_id];
                  const showDateInputs = canEdit && isEditing;
                  return (
                    <tr key={item.activity_id}>
                      <td className="sh_tableCellWrap">{item.activity_name || "-"}</td>
                      <td>{toUiDate(item.last_exam_taken) || "—"}</td>
                      <td>
                        {showDateInputs ? (
                          <FormInputs
                            type="Date"
                            value={draft.due_date}
                            changeHandler={(val) => onChangeDueDateDraft(item.activity_id, val)}
                          />
                        ) : (
                          toUiDate(item.due_date) || "—"
                        )}
                      </td>
                      <td>
                        <span className={statusTextClassMap[item.status] || "sh_dashtext-yellow"}>
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center flex-wrap patient-compliance-exam-actions">
                          <button
                            type="button"
                            className={`patient-compliance-exam-action ${
                              item.reminders_disabled ? "text-muted opacity-50" : ""
                            }`}
                            title={
                              item.reminders_disabled
                                ? "Exam reminders are off — click to enable"
                                : "Mute reminders for this exam"
                            }
                            onClick={() => openReminderConfirm(item)}
                          >
                            <Icons type="Notification" />
                          </button>
                          {canEdit ? (
                            <div className="d-flex align-items-center">
                              {!isEditing ? (
                                <button
                                  type="button"
                                  className="patient-compliance-exam-action"
                                  title="Edit"
                                  onClick={() => startEdit(item.activity_id)}
                                >
                                  <Icons type="Edit" />
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="patient-compliance-exam-action"
                                    title="Save"
                                    onClick={() => saveOverride(item)}
                                  >
                                    <Icons type="Update" width={16} height={16} />
                                  </button>
                                  <button
                                    type="button"
                                    className="patient-compliance-exam-action"
                                    title="Cancel"
                                    onClick={() => cancelEdit(item.activity_id, item)}
                                  >
                                    <Icons type="Cancel" width={14} height={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="patient-compliance-exam-action text-muted opacity-50"
                              title="Manual override is disabled"
                              disabled
                            >
                              <Icons type="Edit" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientComplianceExams;
